import { userBalancePath, userFundsPath, userOrderCheckoutPath, userTopupDiscountPath } from "../../functions/src/admin/paths";
import { PAYMENT_PREP_RETRY_BUDGET_MS } from "../../functions/src/api/constants";
import { isFulfillmentTransientCallableError } from "../../functions/src/common/checkoutFulfillment";
import { isExpectedOrderDeadlineCallableError } from "../../functions/src/common/orderDeadlines";
import { getWalletHistorySchoolYearBounds } from "../../functions/src/api/dates";
import type { Language } from "./langConfig";
import type { FundingRecord, OrderCheckoutSession, PlaceBasketOrdersResult, UserTopupDiscount, VivaPaymentPurpose } from "../../functions/src/types";
import { PlaceOrderPackageParams, PlaceOrderParams } from "../types";
import { getFirebaseCallableErrorCode, isCallableTransportError } from "./callableRetry";
export { getCallableErrorMessage } from "./callableRetry";
import type { DataSnapshot } from "./firebase";
import firebase, { auth, currentDb, currentSchoolKey, functions, functionsEu } from "./firebase";
import { useDatabase, useDatabaseVal } from "./firebaseHooks";
import { useSchoolYearHistorySetup, type SchoolYearHistorySetup } from "./schoolYearHistorySetup";
import { captureException } from "./sentry";
import { syncPackagePayPendingSessionBeforeVivaRedirect } from "../Order/vivaPaymentReturn";
import { useCallback, useMemo } from "react";

/** Client wait for payment URL creation (Viva + cold starts); server should still return ASAP. */
export const TOPUP_CALLABLE_TIMEOUT_MS = 120_000;

type CallWithSentryOptions = { timeoutMs?: number };

const PAYMENT_CALLABLE_NAMES = new Set(['prepareOrderPayment', 'createVivaOrderPayment', 'finalizeOrderPayment', 'cancelOrderPaymentPrep']);
const FULFILLMENT_CALLABLE_NAMES = new Set(['placeBasketOrders', 'placeOrderPackage']);
/** Deployed in europe-west1 next to the RTDB instances (see `functionsEu`); everything else is us-central1. */
const EU_CALLABLE_NAMES = new Set(['initiateOrderCheckout', 'prepareOrderPayment', 'finalizeOrderPayment', 'cancelOrderPaymentPrep']);

function functionsInstanceFor(callableName: string): typeof functions {
    return EU_CALLABLE_NAMES.has(callableName) ? functionsEu : functions;
}

/** Client timeout or connection drop before a callable response — safe to retry the payment flow. */
export function isPaymentCallableTransportError(error: unknown): boolean {
    return isCallableTransportError(error);
}

export { getCheckoutExpiredDates, isCheckoutRemoveExpiredDaysError, isCheckoutResumeFulfillmentError, isFulfillmentConcurrencyError } from "../../functions/src/common/checkoutFulfillment";

/** Another tab is minting a Viva order — server retries internally; client may retry briefly too. */
export function isPaymentBeingPreparedError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }
    const msg = error.message.toLowerCase();
    return msg.includes('payment is being prepared') || msg.includes('πληρωμή προετοιμάζεται');
}

/** Viva OAuth / VPC egress blip — safe to retry prepare → create once or twice. */
export function isPaymentStartTransientError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }
    const msg = error.message.toLowerCase();
    return msg.includes('payment could not be started') || msg.includes('πληρωμή δεν μπόρεσε να ξεκινήσει');
}

/**
 * Post-Viva / webhook timing and fulfillment mutex — retried by BasketProvider / OrderApp.
 * Skip Sentry on intermediate attempts; callers log only after retries exhaust.
 */
export function isFulfillmentTransientError(error: unknown): boolean {
    return isFulfillmentTransientCallableError(error);
}

/** Payment prep / Viva / transport blips — retried by createOrderPaymentWithRetry. */
export function isPaymentTransientError(error: unknown): boolean {
    return isPaymentCallableTransportError(error)
        || isPaymentBeingPreparedError(error)
        || isPaymentStartTransientError(error);
}

const CHECKOUT_CALLABLE_NAMES = new Set(['initiateOrderCheckout']);

function shouldSkipCallableSentryLog(callableName: string, error: unknown): boolean {
    // HTTP status 0 / client timeout: response never reached the browser (server often returned 200).
    // Callers retry or surface the failure in UI; don't log the first attempt for any callable.
    if (isCallableTransportError(error)) {
        return true;
    }
    if (PAYMENT_CALLABLE_NAMES.has(callableName) && isPaymentTransientError(error)) {
        return true;
    }
    if (FULFILLMENT_CALLABLE_NAMES.has(callableName) && isFulfillmentTransientError(error)) {
        return true;
    }
    if (CHECKOUT_CALLABLE_NAMES.has(callableName) && isFulfillmentTransientError(error)) {
        return true;
    }
    if (isExpectedOrderDeadlineCallableError(error)) {
        return true;
    }
    return false;
}

async function callWithSentry<T = any>(callableName: string, payload: Record<string, any>, options?: CallWithSentryOptions): Promise<T> {
    try {
        const functionsInstance = functionsInstanceFor(callableName);
        const callable =
            options?.timeoutMs != null
                ? (functionsInstance as any).httpsCallable(callableName, { timeout: options.timeoutMs })
                : functionsInstance.httpsCallable(callableName);
        return (await callable(payload)).data as T;
    } catch (error) {
        const firebaseCode = getFirebaseCallableErrorCode(error);
        if (shouldSkipCallableSentryLog(callableName, error)) {
            if (isExpectedOrderDeadlineCallableError(error)) {
                console.log(`[funds.ts] ${callableName} rejected (order deadline):`, error);
            } else {
                console.warn(`[funds.ts] ${callableName} transient error (will retry):`, error);
            }
        } else {
            captureException(error instanceof Error ? error : new Error(String(error)), {
                tags: {
                    component: "funds.ts",
                    callableName,
                    ...(firebaseCode ? { firebase_code: firebaseCode } : {}),
                },
                extra: {
                    payloadKeys: Object.keys(payload || {}),
                    uid: auth.currentUser?.uid,
                    schoolKey: currentSchoolKey,
                },
            });
        }
        throw error;
    }
}

export function useFundHistorySnapshot(): DataSnapshot | undefined {
    return useDatabase(auth.currentUser?.uid ? userFundsPath(auth.currentUser.uid) : null);
}

/** Fund ledger rows for one wallet-history school year (1 Aug – 31 Jul), via `timestamp` range query. */
export function useFundHistoryForSchoolYear(schoolYearStartYear: number | null): DataSnapshot | undefined {
    const uid = auth.currentUser?.uid;
    const { startMs, endMs } = useMemo(
        () => (schoolYearStartYear != null ? getWalletHistorySchoolYearBounds(schoolYearStartYear) : { startMs: 0, endMs: 0 }),
        [schoolYearStartYear]
    );
    const filter = useCallback(
        (ref: firebase.database.Reference) => ref.orderByChild("timestamp").startAt(startMs).endAt(endMs),
        [startMs, endMs]
    );
    return useDatabase(uid && schoolYearStartYear != null ? userFundsPath(uid) : null, filter, true);
}

function useFundHistoryLatestSnapshot(): DataSnapshot | undefined | null {
    const uid = auth.currentUser?.uid;
    const filter = useCallback(
        (ref: firebase.database.Reference) => ref.orderByChild("timestamp").limitToLast(1),
        []
    );
    return useDatabase(uid ? userFundsPath(uid) : null, filter, true);
}

function useFundHistoryLatestTimestamp(): number | null | undefined {
    const uid = auth.currentUser?.uid;
    const latestSnapshot = useFundHistoryLatestSnapshot();

    return useMemo((): number | null | undefined => {
        if (!uid) return undefined;
        if (latestSnapshot === undefined || latestSnapshot === null) return undefined;
        if (!latestSnapshot.exists()) return null;
        let latest = 0;
        latestSnapshot.forEach((child) => {
            const entry = child.val() as FundingRecord;
            if (entry?.timestamp > latest) latest = entry.timestamp;
        });
        return latest || null;
    }, [uid, latestSnapshot]);
}

export type WalletHistoryYearSetup = SchoolYearHistorySetup;

/** Registration → current year range; default = school year of most recent transaction. */
export function useWalletHistoryYearSetup(): WalletHistoryYearSetup {
    return useSchoolYearHistorySetup(useFundHistoryLatestTimestamp());
}

export function useBalance(unknownVal = 0): number {
    const balance = +useDatabaseVal<number>(userBalancePath(auth.currentUser?.uid || ''))[0] || unknownVal;

    return isNaN(balance) ? unknownVal : balance;
}

export function useTopupDiscount(uid?: string): UserTopupDiscount | null {
    const targetUid = uid || auth.currentUser?.uid;
    if (!targetUid) return null;

    const [discount] = useDatabaseVal<UserTopupDiscount>(userTopupDiscountPath(targetUid));

    // Check if discount is still valid
    if (discount?.validUntil > Date.now()) {
        return discount;
    }

    return null;
}

export async function placeOrder(params: PlaceOrderParams): Promise<{ error?: string; ok?: boolean; price?: number; ref?: string }> {
    return callWithSentry('placeOrder', params);
}

export type PlaceOrderPackageResult = {
    error?: string
    ok?: boolean
    copyComplete?: boolean
    message?: string
};

export async function placeOrderPackage(params: PlaceOrderPackageParams): Promise<PlaceOrderPackageResult> {
    return callWithSentry('placeOrderPackage', params);
}

// ===== Multi-day basket checkout =====

export type OrderCheckoutQuote = {
    ok: boolean
    days: OrderCheckoutSession['days']
    totalNewPrice: number
    totalRefunds: number
    netDue: number
    creditsUsed: number
    payAmount: number
}

/** Server-side pricing + pending session creation for the given basket dates. */
export async function initiateOrderCheckout(dates: string[], clientLang?: Language): Promise<OrderCheckoutQuote> {
    return callWithSentry('initiateOrderCheckout', {
        dates,
        userDate: Date.now(),
        localDbURL: currentDb.databaseURL,
        ...(clientLang && { clientLang }),
    });
}

/** True when basket registration finished (this call or another path e.g. webhook). */
export function isBasketFulfillmentComplete(result: PlaceBasketOrdersResult | undefined, paid: boolean): boolean {
    if (!result) {
        return false;
    }
    if (result.allCommitted) {
        return true;
    }
    // Paid checkout but session already cleared by webhook / concurrent fulfill
    return !!result.sessionAbsent && paid;
}

/** Registers all uncommitted days of the pending checkout session (per-day independent commits). */
export async function placeBasketOrders(clientLang?: Language): Promise<PlaceBasketOrdersResult> {
    return callWithSentry('placeBasketOrders', {
        userDate: Date.now(),
        localDbURL: currentDb.databaseURL,
        ...(clientLang && { clientLang }),
    });
}

/** Drop a stale checkout session when safe (empty basket, dismiss credits-only error). Server-only RTDB path. */
export async function clearAbandonedOrderCheckout(): Promise<{ cleared: boolean }> {
    return callWithSentry('clearAbandonedOrderCheckout', { localDbURL: currentDb.databaseURL });
}

export { PACKAGE_PAY_PENDING_STORAGE_KEY } from '../Order/vivaPaymentReturn';

async function finalizeOrderPaymentCall(paymentIntentId: string, orderCode: string, reused: boolean): Promise<void> {
    await callWithSentry(
        'finalizeOrderPayment',
        { paymentIntentId, orderCode, reused },
        { timeoutMs: TOPUP_CALLABLE_TIMEOUT_MS },
    );
}

/** Don't re-ping the same callables more often than this; a Cloud Run instance stays warm ~15 min idle. */
const WARM_PAYMENT_CALLABLES_MIN_INTERVAL_MS = 60_000;
let lastWarmPaymentCallablesAt: Record<VivaPaymentPurpose, number> = { topUp: 0, orderCheckout: 0, orderPackage: 0 };

/**
 * Fire-and-forget `{ warm: true }` pings to the callables the Pay button is about to chain, sent when the basket /
 * package popup opens. Boots cold instances and opens their RTDB sockets while the parent is still reading totals
 * (each cold boot is ~2–2.5 s on the critical path otherwise). Zero-cost alternative to `minInstances`; errors are
 * ignored — the real call still works, just without the head start.
 */
export function warmPaymentCallables(purpose: VivaPaymentPurpose): void {
    if (!auth.currentUser) {
        return;
    }
    const now = Date.now();
    if (now - lastWarmPaymentCallablesAt[purpose] < WARM_PAYMENT_CALLABLES_MIN_INTERVAL_MS) {
        return;
    }
    lastWarmPaymentCallablesAt = { ...lastWarmPaymentCallablesAt, [purpose]: now };

    const schoolKey = currentSchoolKey;
    const names = purpose === 'orderCheckout'
        ? ['initiateOrderCheckout', 'prepareOrderPayment', 'finalizeOrderPayment']
        : ['prepareOrderPayment', 'finalizeOrderPayment']; // orderPackage + topUp
    names.forEach(name => {
        functionsInstanceFor(name)
            .httpsCallable(name)({ warm: true, schoolKey, localDbURL: currentDb.databaseURL })
            .catch((error: unknown) => {
                console.warn(`[funds.ts] warm ping ${name} failed (ignored):`, error);
            });
    });
}

/** Purpose-tagged Viva payment (basket checkout / package): prepare → VPC Viva order (finalize inlined when possible). */
export async function createOrderPayment(amount: number, purpose: VivaPaymentPurpose, options?: { packageId?: string }): Promise<void> {
    syncPackagePayPendingSessionBeforeVivaRedirect(purpose, sessionStorage, options);

    const prep = await callWithSentry<{ paymentIntentId: string; intentToken: string }>(
        'prepareOrderPayment',
        { amount, schoolKey: currentSchoolKey, purpose },
        { timeoutMs: TOPUP_CALLABLE_TIMEOUT_MS },
    );

    const vivaResult = await callWithSentry<{ orderCode: string; url: string; reused: boolean; finalized?: boolean }>(
        'createVivaOrderPayment',
        { intentToken: prep.intentToken, paymentIntentId: prep.paymentIntentId },
        { timeoutMs: TOPUP_CALLABLE_TIMEOUT_MS },
    ).catch(async (error) => {
        await callWithSentry('cancelOrderPaymentPrep', { paymentIntentId: prep.paymentIntentId }).catch(() => null);
        throw error;
    });

    // Bookkeeping usually already ran on the warm createVivaOrderPayment instance — skip the extra hop.
    // If it did not (older revision, or RTDB write failed after Viva minted the order), fall back to finalize.
    if (!vivaResult.finalized) {
        try {
            await finalizeOrderPaymentCall(prep.paymentIntentId, vivaResult.orderCode, vivaResult.reused);
        } catch (error) {
            if (!isPaymentTransientError(error)) {
                captureException(error instanceof Error ? error : new Error(String(error)), {
                    tags: { component: 'funds.ts', callableName: 'finalizeOrderPayment' },
                    extra: { paymentIntentId: prep.paymentIntentId, orderCode: vivaResult.orderCode, uid: auth.currentUser?.uid },
                });
            } else {
                console.warn('[funds.ts] finalizeOrderPayment transient error (retrying):', error);
            }
            // Retry once — finalize is idempotent if the first attempt partially succeeded.
            try {
                await finalizeOrderPaymentCall(prep.paymentIntentId, vivaResult.orderCode, vivaResult.reused);
            } catch (retryError) {
                if (!isPaymentTransientError(retryError)) {
                    captureException(retryError instanceof Error ? retryError : new Error(String(retryError)), {
                        tags: { component: 'funds.ts', callableName: 'finalizeOrderPayment', retry: '1' },
                        extra: { paymentIntentId: prep.paymentIntentId, orderCode: vivaResult.orderCode, uid: auth.currentUser?.uid },
                    });
                } else {
                    console.warn('[funds.ts] finalizeOrderPayment transient error after retry (continuing to Viva):', retryError);
                }
                // Payment URL is valid — redirect even if session/transaction bookkeeping failed (webhook can still match).
            }
        }
    }

    window.location.href = vivaResult.url;
}

/**
 * Paid basket/package fulfillment may run before vivaSuccess/webhook credits the wallet — retry these.
 * Transport blips (status 0 after Viva redirect) are retried for any checkout: per-day commits are
 * idempotent and the webhook may already have fulfilled (retry then returns sessionAbsent → success).
 */
export function isBasketFulfillmentRetryable(error: unknown, paid: boolean): boolean {
    return isCallableTransportError(error) || (paid && isFulfillmentTransientError(error));
}

export async function createOrderPaymentWithRetry(amount: number, purpose: VivaPaymentPurpose, options?: { packageId?: string }): Promise<void> {
    // Outlast paymentInitAt lock — a transport blip may succeed server-side and leave the mutex until TTL.
    const mutexRetryDeadline = Date.now() + PAYMENT_PREP_RETRY_BUDGET_MS;
    const shortRetryMaxAttempts = 4;
    let attempt = 0;
    for (; ;) {
        attempt++;
        try {
            await createOrderPayment(amount, purpose, options);
            return;
        } catch (error) {
            if (!isPaymentTransientError(error)) {
                throw error;
            }
            let shouldRetry: boolean;
            let retryDelayMs: number;
            if (isPaymentBeingPreparedError(error)) {
                retryDelayMs = 2000;
                shouldRetry = Date.now() + retryDelayMs < mutexRetryDeadline;
            } else {
                retryDelayMs = isPaymentCallableTransportError(error)
                    ? 2000 * attempt
                    : isPaymentStartTransientError(error)
                        ? 1500 * attempt
                        : 500;
                shouldRetry = attempt < shortRetryMaxAttempts;
            }
            if (shouldRetry) {
                await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                continue;
            }
            // Exhausted retries on transport / mutex blips is expected — UI + console already surface it.
            if (!isPaymentCallableTransportError(error) && !isPaymentBeingPreparedError(error)) {
                const firebaseCode = getFirebaseCallableErrorCode(error);
                captureException(error instanceof Error ? error : new Error(String(error)), {
                    tags: {
                        component: "funds.ts",
                        callableName: "createOrderPayment",
                        ...(firebaseCode ? { firebase_code: firebaseCode } : {}),
                    },
                    extra: { uid: auth.currentUser?.uid, schoolKey: currentSchoolKey, attempts: attempt },
                });
            }
            throw error;
        }
    }
}

/** Live pending basket checkout session (school DB); null = none pending, undefined = loading. */
export function useOrderCheckoutSession(): OrderCheckoutSession | null | undefined {
    const uid = auth.currentUser?.uid;
    const snapshot = useDatabase(uid ? userOrderCheckoutPath(uid) : null);
    if (snapshot === undefined) return undefined;
    return (snapshot?.val() as OrderCheckoutSession) ?? null;
}